import { siteUrl } from "@/lib/utils";
import type {
  DescriptionBlock,
  JobSortOption,
  QaJobDetail,
  QaJobFilters,
  TargetQaPrepLink,
} from "./types";

const MAX_SLUG_BASE_LEN = 76;

export function slugifySegment(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/c#/g, "csharp")
    .replace(/\.net/g, "dotnet")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

/**
 * Builds a deterministic, keyword-rich canonical slug for a QA job:
 * `<title>-<company>-<country>-<12-hex-fingerprint-prefix>`
 */
export function buildJobSlug(input: {
  title: string;
  companyName: string;
  country: string;
  fingerprint: string;
}): string {
  const shortFp = input.fingerprint.trim().toLowerCase().slice(0, 12);
  const titlePart = slugifySegment(input.title).slice(0, 46).replace(/-+$/g, "");
  const companyPart = slugifySegment(input.companyName).slice(0, 20).replace(/-+$/g, "");
  const countryPart = slugifySegment(input.country).slice(0, 14).replace(/-+$/g, "");

  const base = [titlePart, companyPart, countryPart]
    .filter(Boolean)
    .join("-")
    .slice(0, MAX_SLUG_BASE_LEN)
    .replace(/-+$/g, "");

  return base ? `${base}-${shortFp}` : shortFp;
}

export type SlugLookup =
  | { kind: "exact"; fingerprint: string }
  | { kind: "range"; lower: string; upper: string };

/**
 * Strictly validates a dynamic `[slug]` param and converts it into an index-friendly lookup.
 * Rejects any unexpected input before touching the database.
 */
export function parseJobSlugLookup(rawSlug: string): SlugLookup | null {
  const cleaned = rawSlug.trim().toLowerCase();
  if (!cleaned || cleaned.length > 180) return null;

  // Exact 64-char SHA-256 hex fingerprint
  if (/^[0-9a-f]{64}$/.test(cleaned)) {
    return { kind: "exact", fingerprint: cleaned };
  }

  // Slug ending in `-<12-hex>` or standalone 12-hex
  const match = cleaned.match(/(?:^|-)([0-9a-f]{12})$/);
  if (!match) return null;

  const prefix = match[1];
  const prefixNum = BigInt(`0x${prefix}`);
  const nextNum = prefixNum + BigInt(1);
  // If prefix was ffffffffffff, upper bound wraps beyond 12 hex digits -> use "g"
  const upper =
    nextNum > BigInt("0xffffffffffff")
      ? "g"
      : nextNum.toString(16).padStart(12, "0");

  return { kind: "range", lower: prefix, upper };
}

/**
 * Security helper: permits only valid http/https external URLs to prevent
 * javascript:/data: XSS or malformed redirect attacks.
 */
export function sanitizeExternalUrl(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > 2048) return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

export function decodeBasicEntities(text: string): string {
  if (!text) return "";
  return text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const SKILL_PATTERNS: ReadonlyArray<{ label: string; pattern: RegExp }> = [
  { label: "Playwright", pattern: /\bplaywright\b/i },
  { label: "Selenium", pattern: /\bselenium\b|\bwebdriver\b/i },
  { label: "Cypress", pattern: /\bcypress\b/i },
  { label: "Appium", pattern: /\bappium\b/i },
  { label: "REST Assured", pattern: /\brest[\s-]?assured\b/i },
  { label: "Postman", pattern: /\bpostman\b/i },
  { label: "TypeScript", pattern: /\btypescript\b/i },
  { label: "Python", pattern: /\bpython\b|\bpytest\b/i },
  { label: "Java", pattern: /\bjava\b(?!\s*script)/i },
  { label: "JavaScript", pattern: /\bjavascript\b|\bnode\.?js\b/i },
  { label: "C#", pattern: /(?:^|[\s,(/])c#(?=[\s,.)/]|$)|\bcsharp\b|\bnunit\b/i },
  { label: "API Testing", pattern: /\bapi\s+test(?:ing|s)?\b|\brest\s*api\b|\bgraphql\b/i },
  { label: "SQL", pattern: /\bsql\b|\bpostgresql\b|\bmysql\b/i },
  { label: "JMeter", pattern: /\bjmeter\b/i },
  { label: "k6", pattern: /\bk6\b/i },
  { label: "Gatling", pattern: /\bgatling\b|\bloadrunner\b/i },
  { label: "Cucumber / BDD", pattern: /\bcucumber\b|\bbdd\b|\bgherkin\b|\bspecflow\b/i },
  { label: "TestNG / JUnit", pattern: /\btestng\b|\bjunit\b/i },
  { label: "CI/CD", pattern: /\bci\s*\/\s*cd\b|\bjenkins\b|\bgithub\s+actions\b|\bgitlab\s+ci\b|\bazure\s+devops\b/i },
  { label: "Docker", pattern: /\bdocker\b|\bkubernetes\b|\bk8s\b/i },
  { label: "AWS / Cloud", pattern: /\baws\b|\bazure\b|\bgcp\b|\bgoogle\s+cloud\b/i },
  { label: "Performance", pattern: /\bperformance\s+test(?:ing)?\b|\bload\s+test(?:ing)?\b/i },
  { label: "Mobile QA", pattern: /\bandroid\b.*\bios\b|\bios\b.*\bandroid\b|\bmobile\s+test(?:ing)?\b|\bxcuitest\b|\bespresso\b/i },
  { label: "Security QA", pattern: /\bowasp\b|\bpenetration\s+test(?:ing)?\b|\bburp\s+suite\b|\bsecurity\s+test(?:ing)?\b/i },
  { label: "AI / LLM Evals", pattern: /\bllm\b|\bgenai\b|\bgenerative\s+ai\b|\bai\s+test(?:ing)?\b/i },
];

export function extractJobSkills(
  title: string,
  text: string,
  category?: string,
  maxSkills = 6,
): string[] {
  const hayTitle = title || "";
  const hayBody = `${hayTitle}\n${text || ""}`;
  const found: string[] = [];

  // Prioritize skills explicitly mentioned in the job title first
  for (const item of SKILL_PATTERNS) {
    if (item.pattern.test(hayTitle) && !found.includes(item.label)) {
      found.push(item.label);
      if (found.length >= maxSkills) return found;
    }
  }

  for (const item of SKILL_PATTERNS) {
    if (item.pattern.test(hayBody) && !found.includes(item.label)) {
      found.push(item.label);
      if (found.length >= maxSkills) return found;
    }
  }

  if (found.length === 0 && category) {
    if (category.includes("SDET")) found.push("Test Automation");
    else if (category.includes("Manual")) found.push("Functional QA");
    else if (category.includes("Performance")) found.push("Performance");
    else if (category.includes("API")) found.push("API Testing");
    else if (category.includes("Security")) found.push("Security QA");
    else if (category.includes("Leadership")) found.push("QA Strategy");
  }

  return found;
}

/**
 * Maps detected job skills & category to relevant free TargetQA practice labs,
 * online compilers, and interview banks — creating high-value UX + strong internal SEO links.
 */
export function mapSkillsToTargetQaResources(
  skills: string[],
  qaCategory: string,
): TargetQaPrepLink[] {
  const links: TargetQaPrepLink[] = [];
  const add = (link: TargetQaPrepLink) => {
    if (!links.some((item) => item.href === link.href) && links.length < 4) {
      links.push(link);
    }
  };

  const has = (label: string) => skills.some((s) => s.toLowerCase().includes(label.toLowerCase()));

  if (has("Playwright")) {
    add({
      title: "Playwright Hands-On Practice",
      subtitle: "Auto-waiting, role locators, and network mocking challenges in cloud browsers.",
      href: "/playwright",
      badge: "Practice Track",
    });
    add({
      title: "Playwright Interview Questions",
      subtitle: "Scenario-based Q&A covering fixtures, contexts, trace viewer, and CI stability.",
      href: "/interview/playwright",
      badge: "Interview Bank",
    });
  }

  if (has("Selenium")) {
    add({
      title: "Selenium WebDriver Practice Labs",
      subtitle: "Explicit waits, dynamic tables, iframes, and Page Object Model exercises.",
      href: "/selenium",
      badge: "Practice Track",
    });
    add({
      title: "Selenium Interview Questions",
      subtitle: "In-depth WebDriver architecture, synchronization, and coding round scenarios.",
      href: "/interview/selenium",
      badge: "Interview Bank",
    });
  }

  if (has("Cypress")) {
    add({
      title: "Cypress E2E Testing Track",
      subtitle: "Practice intercepts, custom commands, and async DOM assertions.",
      href: "/cypress",
      badge: "Practice Track",
    });
  }

  if (has("API") || has("REST Assured") || has("Postman") || qaCategory.includes("API")) {
    add({
      title: "API & Contract Testing Track",
      subtitle: "Validate REST schemas, status assertions, auth tokens, and payload contracts.",
      href: "/api-testing",
      badge: "API Track",
    });
  }

  if (has("SQL")) {
    add({
      title: "SQL & Database Testing Questions",
      subtitle: "Joins, ACID integrity, and backend verification queries asked in QA interviews.",
      href: "/interview/sql",
      badge: "SQL Prep",
    });
  }

  if (has("Performance") || has("JMeter") || has("k6") || qaCategory.includes("Performance")) {
    add({
      title: "Performance & k6 Load Testing",
      subtitle: "Virtual user load profiles, p95 latency thresholds, and bottleneck analysis.",
      href: "/performance-testing",
      badge: "Performance",
    });
  }

  if (qaCategory.includes("Manual")) {
    add({
      title: "Manual & Functional QA Interview Prep",
      subtitle: "Test design techniques, defect lifecycle, boundary analysis, and agile QA.",
      href: "/interview/manual-testing",
      badge: "Interview Prep",
    });
  }

  add({
    title: "700+ SDET & QA Coding Challenges",
    subtitle: "Solve real browser automation problems in Java, Python, TypeScript, and C#.",
    href: "/challenges",
    badge: "Coding Labs",
  });

  add({
    title: "Real-Time QA Automation Scenarios",
    subtitle: "Prepare for senior SDET architecture and live debugging interview rounds.",
    href: "/interview/automation-real-time",
    badge: "Interview Hub",
  });

  return links;
}

const HEADING_KEYWORDS =
  /^(?:company\s+overview|overview|about\s+(?:us|the\s+role|the\s+team|the\s+job|the\s+company)|role\s+summary|job\s+summary|job\s+description|position\s+summary|what\s+you(?:'ll|\s+will)\s+(?:do|be\s+doing|bring)|key\s+responsibilities|responsibilities|accountabilities|duties|your\s+impact|requirements|minimum\s+qualifications|preferred\s+qualifications|basic\s+qualifications|qualifications|who\s+you\s+are|skills(?:\s+&\s+experience|\s+and\s+qualifications)?|must\s+have|nice\s+to\s+have|bonus\s+points|what\s+we\s+(?:offer|look\s+for)|benefits(?:\s+&\s+perks)?|why\s+join\s+us|tech\s+stack|our\s+stack)[:\s]*$/i;

function isLikelySectionHeading(line: string): boolean {
  if (line.length < 3 || line.length > 72) return false;
  if (/[.!?]$/.test(line)) return false;
  if (HEADING_KEYWORDS.test(line)) return true;
  if (line.endsWith(":") && line.split(/\s+/).length <= 8) return true;
  return false;
}

/**
 * Safely parses plain-text ATS job descriptions into structured semantic blocks
 * (headings, paragraphs, and consolidated bullet lists) without ever using raw HTML.
 */
export function parseJobDescriptionBlocks(rawDescription: string): DescriptionBlock[] {
  const decoded = decodeBasicEntities(rawDescription || "").replace(/\r\n/g, "\n");
  if (!decoded.trim()) return [];

  const rawLines = decoded.split("\n").map((l) => l.trim());

  // Join standalone bullet markers (`•`, `-`, `*`, `·`) with the next non-empty line
  // (common in Lever / Workday / LinkedIn scraped descriptions).
  const normalizedLines: string[] = [];
  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i];
    if (/^[•·▪◦\-*]$/.test(line)) {
      let j = i + 1;
      while (j < rawLines.length && rawLines[j] === "") j++;
      if (j < rawLines.length && !/^[•·▪◦\-*]$/.test(rawLines[j])) {
        normalizedLines.push(`• ${rawLines[j]}`);
        i = j;
        continue;
      }
    }
    normalizedLines.push(line);
  }

  const blocks: DescriptionBlock[] = [];
  let paragraphBuf: string[] = [];
  let bulletBuf: string[] = [];

  const flushParagraph = () => {
    if (paragraphBuf.length > 0) {
      blocks.push({ kind: "paragraph", text: paragraphBuf.join(" ") });
      paragraphBuf = [];
    }
  };

  const flushBullets = () => {
    if (bulletBuf.length > 0) {
      blocks.push({ kind: "bullets", items: bulletBuf });
      bulletBuf = [];
    }
  };

  for (const line of normalizedLines) {
    if (!line) {
      flushParagraph();
      continue;
    }

    const bulletMatch = line.match(/^(?:[•·▪◦*]|-(?=\s)|\d{1,2}[.)]\s)\s*(.+)$/);
    if (bulletMatch) {
      flushParagraph();
      const item = bulletMatch[1].trim();
      if (item) bulletBuf.push(item);
      continue;
    }

    if (isLikelySectionHeading(line)) {
      flushParagraph();
      flushBullets();
      blocks.push({ kind: "heading", text: line.replace(/:$/, "").trim() });
      continue;
    }

    flushBullets();
    paragraphBuf.push(line);
  }

  flushParagraph();
  flushBullets();

  return blocks;
}

/**
 * Generates clean, escaped HTML (`<p>`, `<h3>`, `<ul><li>`) strictly for Google's
 * `JobPosting.description` JSON-LD specification.
 */
export function buildGoogleJobHtmlDescription(blocks: DescriptionBlock[], fallback: string): string {
  if (blocks.length === 0) {
    return `<p>${escapeHtml(decodeBasicEntities(fallback || "Quality Assurance role."))}</p>`;
  }
  return blocks
    .map((block) => {
      if (block.kind === "heading") {
        return `<h3>${escapeHtml(block.text)}</h3>`;
      }
      if (block.kind === "bullets") {
        const items = block.items.map((item) => `<li>${escapeHtml(item)}</li>`).join("");
        return `<ul>${items}</ul>`;
      }
      return `<p>${escapeHtml(block.text)}</p>`;
    })
    .join("");
}

/** Normalizes ATS employment types to Google's official schema.org/JobPosting enum. */
export function normalizeGoogleEmploymentType(raw: string | null | undefined): string {
  const v = (raw || "").toLowerCase();
  if (v.includes("intern")) return "INTERN";
  if (v.includes("part")) return "PART_TIME";
  if (v.includes("contract") || v.includes("freelance")) return "CONTRACTOR";
  if (v.includes("temp")) return "TEMPORARY";
  return "FULL_TIME";
}

export function formatEmploymentLabel(raw: string | null | undefined): string {
  const norm = normalizeGoogleEmploymentType(raw);
  switch (norm) {
    case "INTERN":
      return "Internship";
    case "PART_TIME":
      return "Part-time";
    case "CONTRACTOR":
      return "Contract";
    case "TEMPORARY":
      return "Temporary";
    default:
      return "Full-time";
  }
}

export function formatAtsName(atsType: string): string {
  const key = (atsType || "").toUpperCase();
  const map: Record<string, string> = {
    LINKEDIN: "LinkedIn",
    GREENHOUSE: "Greenhouse",
    LEVER: "Lever",
    WORKDAY: "Workday",
    SMARTRECRUITERS: "SmartRecruiters",
    ASHBY: "Ashby",
    BAMBOOHR: "BambooHR",
    WORKABLE: "Workable",
    BUILTIN: "Built In",
    HIMALAYAS: "Himalayas",
    KEKA: "Keka",
    ARBEITNOW: "Arbeitnow",
    AMAZON: "Amazon Jobs",
  };
  return map[key] ?? (atsType ? atsType.charAt(0).toUpperCase() + atsType.slice(1).toLowerCase() : "Company Portal");
}

export function formatLocationLabel(city: string, country: string): string {
  const cleanCity = (city || "").trim();
  const cleanCountry = (country || "").trim();
  if (!cleanCity || cleanCity.toLowerCase() === cleanCountry.toLowerCase() || cleanCity === "US") {
    return cleanCountry || "Global";
  }
  if (cleanCountry && cleanCity.toLowerCase().endsWith(cleanCountry.toLowerCase())) {
    return cleanCity;
  }
  return cleanCountry ? `${cleanCity}, ${cleanCountry}` : cleanCity;
}

export function formatExperienceShort(
  experienceLevel: string,
  minYears: number | null,
  maxYears: number | null,
): string {
  if (minYears !== null && maxYears !== null && maxYears >= minYears) {
    return `${minYears}–${maxYears} yrs`;
  }
  if (minYears !== null && minYears > 0) {
    return `${minYears}+ yrs`;
  }
  if (experienceLevel && experienceLevel !== "Not Specified") {
    return experienceLevel;
  }
  return "Open Experience";
}

export function formatHeadcountBadge(headcount: number | null): string | null {
  if (!headcount || headcount <= 0) return null;
  if (headcount >= 10000) return `${Math.round(headcount / 1000).toLocaleString("en-US")}k+ employees`;
  if (headcount >= 1000) return `${(headcount / 1000).toFixed(1).replace(/\.0$/, "")}k+ employees`;
  return `${headcount.toLocaleString("en-US")}+ employees`;
}

export function formatRelativePosted(isoDate: string | null): string {
  if (!isoDate) return "Recently verified";
  const parsed = Date.parse(isoDate);
  if (Number.isNaN(parsed)) return "Recently verified";
  const diffMs = Date.now() - parsed;
  if (diffMs < 0) return "Just posted";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return mins <= 5 ? "Just now" : `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export function formatExactDate(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const parsed = Date.parse(isoDate);
  if (Number.isNaN(parsed)) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(parsed));
}

/** Builds clean query string hrefs for `/qa-jobs` preserving only active non-default filters. */
export function buildJobsHref(overrides: Partial<QaJobFilters>, base?: QaJobFilters): string {
  const merged: QaJobFilters = {
    q: base?.q ?? "",
    country: base?.country ?? "",
    category: base?.category ?? "",
    level: base?.level ?? "",
    workplace: base?.workplace ?? "",
    maxExp: base?.maxExp ?? null,
    ats: base?.ats ?? "",
    sort: (base?.sort ?? "newest") as JobSortOption,
    page: base?.page ?? 1,
    ...overrides,
  };

  const params = new URLSearchParams();
  if (merged.q.trim()) params.set("q", merged.q.trim());
  if (merged.country.trim()) params.set("country", merged.country.trim());
  if (merged.category.trim()) params.set("category", merged.category.trim());
  if (merged.level.trim()) params.set("level", merged.level.trim());
  if (merged.workplace.trim()) params.set("workplace", merged.workplace.trim());
  if (merged.maxExp !== null && merged.maxExp >= 0) params.set("maxExp", String(merged.maxExp));
  if (merged.ats.trim()) params.set("ats", merged.ats.trim());
  if (merged.sort && merged.sort !== "newest") params.set("sort", merged.sort);
  if (merged.page > 1) params.set("page", String(merged.page));

  const qs = params.toString();
  return qs ? `/qa-jobs?${qs}` : "/qa-jobs";
}

/**
 * Auto-SEO Builder: Produces a Google Jobs compliant `schema.org/JobPosting` object
 * for every individual QA job detail page.
 */
export function buildJobPostingJsonLd(job: QaJobDetail, blocks: DescriptionBlock[]) {
  const canonicalUrl = `${siteUrl}/qa-jobs/${job.slug}`;
  const postedDateObj =
    job.postedAt && !Number.isNaN(Date.parse(job.postedAt))
      ? new Date(job.postedAt)
      : new Date();
  const datePostedIso = postedDateObj.toISOString();

  const validThroughIso =
    job.validThrough && !Number.isNaN(Date.parse(job.validThrough))
      ? new Date(job.validThrough).toISOString()
      : new Date(postedDateObj.getTime() + 45 * 24 * 60 * 60 * 1000).toISOString();

  const isRemote =
    job.workplaceType.toLowerCase() === "remote" ||
    job.country.toLowerCase().includes("remote");

  const cleanCountry =
    job.country && job.country !== "Global Remote" && job.country !== "Other"
      ? job.country
      : "US";
  const cleanLocality =
    job.city && job.city !== job.country ? job.city.split(",")[0].trim() : cleanCountry;

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "@id": `${canonicalUrl}#job`,
    url: canonicalUrl,
    title: job.title,
    description: buildGoogleJobHtmlDescription(blocks, job.descriptionSnippet),
    identifier: {
      "@type": "PropertyValue",
      name: job.companyName,
      value: job.fingerprint.slice(0, 16),
    },
    datePosted: datePostedIso,
    validThrough: validThroughIso,
    employmentType: normalizeGoogleEmploymentType(job.employmentType),
    hiringOrganization: {
      "@type": "Organization",
      name: job.companyName,
    },
    industry: "Software Quality Assurance & Test Engineering",
    occupationalCategory: job.qaCategory,
    directApply: false,
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: cleanLocality,
        addressCountry: cleanCountry,
      },
    },
  };

  if (isRemote) {
    schema.jobLocationType = "TELECOMMUTE";
    if (job.country && job.country !== "Global Remote" && job.country !== "Other") {
      schema.applicantLocationRequirements = {
        "@type": "Country",
        name: job.country,
      };
    }
  }

  if (job.minYears !== null && job.minYears >= 0) {
    schema.experienceRequirements =
      job.minYears === 0
        ? "no requirements"
        : {
            "@type": "OccupationalExperienceRequirements",
            monthsOfExperience: Math.round(job.minYears * 12),
          };
  }

  if (job.skills.length > 0) {
    schema.skills = job.skills.join(", ");
  }

  return schema;
}
